import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { useTranslation } from 'react-i18next';

function MentoringAgreementTile({ user, matchingId }) {
  const [agreement, setAgreement] = useState(null);
  const [menteeName, setMenteeName] = useState('');
  const [mentorName, setMentorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!user?.user_id) return;
    if (!matchingId) {
      setLoading(false);
      setAgreement(null);
      return;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setAgreement(null);

      try {
        const res = await axios.get(
          `/mentoring/agreement/mentee/${user.user_id}?matching_id=${matchingId}`
        );

        if (cancelled) return;

        // API kann { exists:false, agreement:null } zurückgeben
        if (res?.data && res.data.exists === false) {
          setAgreement(null);
          return;
        }

        // Rückwärtskompatibel: entweder steckt das Objekt unter .agreement oder direkt in data
        const a = res?.data?.agreement ?? res?.data ?? null;

        if (!a) {
          setAgreement(null);
          return;
        }

        setAgreement(a);

        // Namen nur laden, wenn beide E-Mails vorhanden
        if (a.mentor_email && a.mentee_email) {
          const names = await axios.get(
            `/mentoring/agreement-names?menteeEmail=${a.mentee_email}&mentorEmail=${a.mentor_email}`
          );
          if (!cancelled && names?.data) {
            setMenteeName(`${names.data.mentee.first_name} ${names.data.mentee.last_name}`);
            setMentorName(`${names.data.mentor.first_name} ${names.data.mentor.last_name}`);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setAgreement(null);
          setError(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user, matchingId]);

  if (loading) {
    return (
      <div className="bg-gray-800 p-6 rounded-2xl shadow text-white">
        <h2 className="font-bold mb-2">{t('mentoringAgreementTile.heading')}</h2>
        <p className="text-sm text-gray-400">Lade Vereinbarung…</p>
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="bg-gray-800 p-6 rounded-2xl shadow text-white">
        <h2 className="font-bold mb-2">{t('mentoringAgreementTile.heading')}</h2>
        <p className="text-sm text-gray-400">{t('mentoringAgreementTile.none')}</p>
        {error ? (
          <p className="text-xs text-red-300 mt-2">({String(error?.message || 'Fehler beim Laden')})</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow text-white">
      <h2 className="font-bold mb-4 text-xl">{t('mentoringAgreementTile.heading')}</h2>
      <div className="space-y-2 text-sm text-gray-200">
        <p className="text-sm text-gray-400 mb-4">{t('mentoringAgreementTile.intro')}</p>
        <p><strong>{t('mentoringAgreementTile.mentor')}:</strong> {mentorName}</p>
        <p><strong>{t('mentoringAgreementTile.mentee')}:</strong> {menteeName}</p>
        <p><strong>{t('mentoringAgreementTile.period')}:</strong> {agreement.period}</p>
        <p><strong>{t('mentoringAgreementTile.turnus')}:</strong> {agreement.turnus}</p>
        <p><strong>{t('mentoringAgreementTile.contactOption')}:</strong> {agreement.contact_option}</p>
        <p><strong>{t('mentoringAgreementTile.location')}:</strong> {agreement.location}</p>

        <div>
          <p className="font-semibold">{t('mentoringAgreementTile.goalsTitle')}</p>
          <ul className="list-disc list-inside">
            {(agreement.goals || []).map((goal, i) => <li key={i}>{goal}</li>)}
          </ul>
        </div>

        <div>
          <p className="font-semibold">{t('mentoringAgreementTile.topicsTitle')}</p>
          <ul className="list-disc list-inside">
            {(agreement.topics || []).map((topic, i) => <li key={i}>{topic}</li>)}
          </ul>
        </div>

        <div>
          <p className="font-semibold">{t('mentoringAgreementTile.motivationTitle')}</p>
          <p>{agreement.why_mentoring}</p>
        </div>

        <section className="space-y-4 text-sm text-gray-300 pt-6">
          <p>{t('mentoringAgreementTile.guidelinesText')}</p>
          <ul className="list-disc list-inside">
            <li>{t('mentoringAgreementTile.guideline1')}</li>
            <li>{t('mentoringAgreementTile.guideline2')}</li>
            <li>{t('mentoringAgreementTile.guideline3')}</li>
            <li>{t('mentoringAgreementTile.guideline4')}</li>
            <li>{t('mentoringAgreementTile.guideline5')}</li>
            <li>{t('mentoringAgreementTile.guideline6')}</li>
            <li>{t('mentoringAgreementTile.guideline7')}</li>
          </ul>
        </section>

        <div className="pt-4 text-xs text-gray-400">
          <p>{t('mentoringAgreementTile.confirmedByMentee')}</p>
          {agreement.mentor_confirmed
            ? <p className="text-green-400">{t('mentoringAgreementTile.confirmedByMentor')}</p>
            : <p className="text-yellow-400">{t('mentoringAgreementTile.notConfirmedByMentor')}</p>}
        </div>
      </div>
    </div>
  );
}

export default MentoringAgreementTile;
